/**
 * FrameworkRoll — editorial replacement for the dense compliance-frameworks
 * table on the Executive Report.
 *
 * Each framework is rendered as a row that leads with the named subject
 * (framework name + its citation code), then the verdict (italic dropcap
 * score + delta chip + control coverage). Severity-tinted dropcaps mirror
 * the KpiStrip pattern: green for healthy, sand for soft, red for low.
 *
 * Frameworks marked `includeInGuardianScore` carry a small "In score" pill
 * so the executive sees which signals are folded into the composite. We
 * still surface frameworks that aren't starred — they count toward audit
 * readiness even when they don't pull on the score.
 */

import type { ExecutiveReportData } from '@/types/executiveReport'

type Framework = ExecutiveReportData['frameworkScores'][number]

interface Props {
  frameworks: Framework[]
}

/** Severity tint for the dropcap score. Same threshold the KpiStrip uses,
 *  matched against the executive band: >= 80 strong, 60-79 soft, < 60 weak. */
function scoreSeverity(score: number): 'strong' | 'soft' | 'weak' {
  if (score >= 80) return 'strong'
  if (score >= 60) return 'soft'
  return 'weak'
}

const SEVERITY_COLOR: Record<'strong' | 'soft' | 'weak', string> = {
  strong: 'rgb(var(--color-primary-500))',
  soft: 'rgb(var(--color-secondary-sand-500))',
  weak: '#C84B4B',
}

/** Verdict copy keyed to severity — short, named-subject-led. */
function frameworkVerdict(name: string, severity: 'strong' | 'soft' | 'weak'): string {
  switch (severity) {
    case 'strong':
      return `${name} is in good shape.`
    case 'soft':
      return `${name} has soft spots to close.`
    case 'weak':
      return `${name} needs eyes on it.`
  }
}

export default function FrameworkRoll({ frameworks }: Props) {
  if (!frameworks || frameworks.length === 0) {
    return (
      <p
        className="text-[14px] italic"
        style={{ color: 'rgb(var(--color-text-secondary))' }}
      >
        No starred frameworks for this period. Star a framework on the
        Standards page to see it surface here.
      </p>
    )
  }

  // Order by severity (weakest first) so the executive eye lands on what
  // needs attention before the all-green frameworks. Ties broken by score
  // ascending so the worst pairing leads.
  const ordered = [...frameworks].sort((a, b) => {
    const sa = scoreSeverity(a.score)
    const sb = scoreSeverity(b.score)
    const rank: Record<typeof sa, number> = { weak: 0, soft: 1, strong: 2 }
    if (rank[sa] !== rank[sb]) return rank[sa] - rank[sb]
    return a.score - b.score
  })

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {ordered.map(f => (
        <FrameworkRow key={f.code} framework={f} />
      ))}
    </div>
  )
}

function FrameworkRow({ framework }: { framework: Framework }) {
  const severity = scoreSeverity(framework.score)
  const numeralColor = SEVERITY_COLOR[severity]
  const verdict = frameworkVerdict(framework.name, severity)

  const deltaChip = (() => {
    if (framework.change == null) return null
    const sign = framework.change > 0 ? '▲' : framework.change < 0 ? '▼' : '·'
    const tone =
      framework.direction === 'up'
        ? 'rgb(var(--color-primary-500))'
        : framework.direction === 'down'
          ? '#C84B4B'
          : 'rgb(var(--color-text-secondary))'
    const value = `${framework.change > 0 ? '+' : ''}${framework.change.toFixed(1)}`
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.16em]"
        style={{ color: tone }}
      >
        <span aria-hidden>{sign}</span>
        <span className="font-mono">{value} pts</span>
      </span>
    )
  })()

  return (
    <article
      className="
        flex items-center gap-5 rounded-lg border bg-dark-800 p-5
        transition-colors duration-150
      "
      style={{ borderColor: 'rgb(var(--color-dark-600))' }}
    >
      {/* Italic dropcap numeral — same display-italic treatment KpiStrip uses. */}
      <div
        className="font-display font-extrabold italic"
        style={{
          fontSize: 'clamp(40px, 4.6vw, 56px)',
          lineHeight: 0.9,
          letterSpacing: '-0.04em',
          color: numeralColor,
          minWidth: '70px',
        }}
      >
        {Math.round(framework.score)}
        <span
          className="ml-1 text-[18px] font-semibold"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          %
        </span>
      </div>

      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span
            className="text-[15px] font-bold leading-tight"
            style={{ color: 'rgb(var(--color-text-primary))' }}
          >
            {framework.name}
          </span>
          <span
            className="font-mono text-[10px] font-medium uppercase tracking-[0.16em]"
            style={{ color: 'rgb(var(--color-text-secondary))' }}
          >
            {framework.code}
          </span>
          {framework.includeInGuardianScore && (
            <span
              className="
                inline-flex items-center rounded-full px-2 py-0.5
                text-[9px] font-bold uppercase tracking-[0.12em]
              "
              style={{
                background: 'rgb(var(--color-primary-500) / 0.15)',
                color: 'rgb(var(--color-primary-700))',
              }}
            >
              In score
            </span>
          )}
        </div>

        <div
          className="text-[13px] italic leading-snug"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          {verdict}
        </div>

        <div
          className="mt-1 flex flex-wrap items-center gap-3 text-[11px]"
          style={{ color: 'rgb(var(--color-text-secondary))' }}
        >
          <span>
            <span className="font-semibold" style={{ color: 'rgb(var(--color-text-primary))' }}>
              {framework.passedControls}
            </span>{' '}
            of {framework.totalControls} controls passing
          </span>
          {deltaChip}
        </div>
      </div>
    </article>
  )
}
